/**
 * Historical evidence. Restrained on purpose: no composite confidence score, no
 * badges — the point is that the numbers are checkable (PRD §13).
 */
import { useEffect, useState } from 'react';
import { api, ApiRequestError } from '../lib/api';
import { Card, Empty, ErrorNote, Label, Loading, Screen } from '../components/ui';
import type { ProgressResponse, WeeklyPoint } from '../../shared/types';

function Sparkline({ points, invert = false }: { points: WeeklyPoint[]; invert?: boolean }) {
  if (points.length < 2) return null;
  const values = points.map((point) => point.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const path = points
    .map((point, index) => {
      const x = (index / (points.length - 1)) * 100;
      const y = 30 - ((point.value - min) / span) * 26 - 2;
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  const first = values[0] ?? 0;
  const last = values[values.length - 1] ?? 0;
  const improving = invert ? last < first : last > first;

  return (
    <svg viewBox="0 0 100 30" preserveAspectRatio="none" className="h-10 w-full" aria-hidden>
      <path
        d={path}
        fill="none"
        stroke={improving ? 'var(--color-signal-400)' : 'var(--color-mist-400)'}
        strokeWidth={1.6}
        vectorEffect="non-scaling-stroke"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Series({
  label,
  points,
  suffix = '',
  invert,
}: {
  label: string;
  points: WeeklyPoint[];
  suffix?: string;
  invert?: boolean;
}) {
  const first = points[0];
  const last = points[points.length - 1];
  return (
    <Card className="flex flex-col gap-2">
      <div className="flex items-baseline justify-between">
        <Label>{label}</Label>
        {first && last ? (
          <span className="text-sm text-mist-200">
            {first.value}
            {suffix} → {last.value}
            {suffix}
          </span>
        ) : (
          <span className="text-sm text-mist-400">not enough data</span>
        )}
      </div>
      <Sparkline points={points} {...(invert === undefined ? {} : { invert })} />
    </Card>
  );
}

export function Progress() {
  const [data, setData] = useState<ProgressResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const alive = { current: true };
    void (async () => {
      try {
        const loaded = await api.progress();
        if (alive.current) setData(loaded);
      } catch (caught) {
        if (alive.current) {
          setError(caught instanceof ApiRequestError ? caught.message : 'Could not load progress.');
        }
      }
    })();
    return () => {
      alive.current = false;
    };
  }, []);

  if (!data) {
    return (
      <Screen title="Progress">
        {error ? <ErrorNote>{error}</ErrorNote> : <Loading label="Counting reps" />}
      </Screen>
    );
  }

  const { metrics, callouts } = data;

  if (metrics.completedAllTime === 0) {
    return (
      <Screen title="Progress">
        <Empty title="Nothing to show yet.">Evidence starts after the first rep.</Empty>
      </Screen>
    );
  }

  return (
    <Screen title="Progress">
      {callouts.length > 0 ? (
        <Card className="flex flex-col gap-2">
          {callouts.map((callout) => (
            <p key={callout} className="text-base text-mist-200">
              {callout}
            </p>
          ))}
        </Card>
      ) : null}

      <Card className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl font-semibold text-mist-200">{metrics.completedToday}</p>
          <Label>today</Label>
        </div>
        <div>
          <p className="text-2xl font-semibold text-mist-200">{metrics.completedThisWeek}</p>
          <Label>this week</Label>
        </div>
        <div>
          <p className="text-2xl font-semibold text-mist-200">{metrics.completedAllTime}</p>
          <Label>all time</Label>
        </div>
      </Card>

      <Series label="Pre-call anxiety by week" points={metrics.anxietyBeforeByWeek} invert />
      <Series label="Post-call anxiety by week" points={metrics.anxietyAfterByWeek} invert />
      <Series
        label="Hesitation (seconds) by week"
        points={metrics.hesitationByWeek}
        suffix="s"
        invert
      />

      <Card className="flex flex-col gap-3">
        <Label>Difficulty over time</Label>
        {metrics.difficultyByWeek.length === 0 ? (
          <p className="text-sm text-mist-400">No completed reps yet.</p>
        ) : (
          <ul className="flex flex-col gap-1.5 text-sm text-mist-400">
            {metrics.difficultyByWeek.map((week) => (
              <li key={week.weekStart} className="flex justify-between gap-3">
                <span>{week.weekStart}</span>
                <span className="text-mist-200">
                  ask {week.mean.ask.toFixed(1)} · rejection {week.mean.rejection.toFixed(1)} ·
                  status {week.mean.status.toFixed(1)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="flex flex-col gap-3">
        <Label>Hesitation distribution</Label>
        <ul className="flex flex-col gap-1.5">
          {Object.entries(metrics.hesitationDistribution).map(([bucket, count]) => (
            <li key={bucket} className="flex items-center gap-3 text-sm">
              <span className="w-16 text-mist-400">{bucket}</span>
              <span className="h-2 flex-1 rounded-full bg-ink-700">
                <span
                  className="block h-2 rounded-full bg-signal-500"
                  style={{
                    width: `${
                      metrics.completedAllTime === 0
                        ? 0
                        : Math.round((count / metrics.completedAllTime) * 100)
                    }%`,
                  }}
                />
              </span>
              <span className="w-6 text-right text-mist-200">{count}</span>
            </li>
          ))}
        </ul>
      </Card>

      <Card className="flex flex-col gap-2">
        <Label>Stretch goals</Label>
        <p className="text-base text-mist-200">
          {metrics.stretchCompleted} completed of {metrics.stretchAttempted} attempted
        </p>
      </Card>
    </Screen>
  );
}
